import { CountryCode, Locale } from '@brenger/api-client';
import { LOCATION_CHANGE } from 'connected-react-router';
import { GeneralFlowActionTypes } from '../modules/GeneralFlow/typings';
import { getWhatIsMyCountry, actions as userActions } from '../modules/User/ducks';
import { UserActionTypes } from '../modules/User/typings';
import { retrieveCurrentUser } from '../modules/User/utils/retrieveCurrentUser';
import { BaseActionTypes } from '../types';
import { getActiveLanguageSettings } from '../utils/localization';
import { coreClient, indicativePriceClient, priceClient, routePlannerClient } from '../utils/request';
import { actions as baseActions } from './ducks/baseReducer';
import { startAppListening } from './middlewares/listener';

function foldInMenuAndFlowClass(): void {
  const navItems = document.querySelectorAll('.main-nav--items .nav-item');
  for (let i = 0; i < navItems.length; ++i) {
    navItems[i].classList.remove('sub-nav--is-open');
  }
  // add class when we are in the new flow
  const body = document.querySelector('body');
  if (!body) {
    return;
  }
  if (window.location.pathname.indexOf(`/customer`) > -1) {
    body.classList.add('render-app');
  } else {
    body.classList.remove('render-app');
  }
}

function setPricingLocale(lang: Locale, countryCode: CountryCode): void {
  // So for pricing we would like to know if we are in Belgium and if we are dutch
  if (countryCode === 'BE' && lang === 'nl-NL') {
    // In this case we switch to flemish / dutch to better match product selection results
    lang = 'nl-BE';
  }
  priceClient.updateDefaultHeaders({ 'X-User-Locale': lang });
  indicativePriceClient.updateDefaultHeaders({ 'X-User-Locale': lang });
}

const QA_ACTION_TYPES: string[] = [
  BaseActionTypes.STOP_APP_LOADING,
  GeneralFlowActionTypes.SET_PRICE_LOADING,
  GeneralFlowActionTypes.SET_QUOTE,
];

/**
 * Registers the app-level side effects (same behavior as the former baseSagas).
 */
export const startBaseListeners = (): void => {
  /**
   * Sets the locale headers, waits for the translations, loads the user and stops the loader; the latest start wins
   * (same behavior as the former getInitialData saga).
   */
  startAppListening({
    type: BaseActionTypes.START_APP_LOADING,
    effect: async (_action, api) => {
      api.cancelActiveListeners();
      // First figur out what country by IP we are dealing with
      api.dispatch(userActions.retrieveCurrentCountry());
      // Setup language header
      // - For CORE & GEO we stick to the languages we support
      // - For PRICING we want to make one exception: Belgium.
      const lang: Locale = getActiveLanguageSettings().labels.full;
      // Set core & geo lang
      coreClient.updateDefaultHeaders({ 'X-User-Locale': lang });
      routePlannerClient.updateDefaultHeaders({ 'X-User-Locale': lang });
      // Set pricing lang (set again by the listener below once the country lookup lands)
      setPricingLocale(lang, getWhatIsMyCountry(api.getState()));

      // Wait for translation payload
      await api.take(action => action.type === '@@localize/ADD_TRANSLATION_FOR_LANGUAGE');
      // Kick off user loading (loads profile + sandbox path when needed; dispatches SET_USER_DETAILS)
      await api.pause(retrieveCurrentUser(api.signal));
      // Done loading
      api.dispatch(baseActions.stopLoading());
    },
  });

  /**
   * Sets the pricing locale again once the IP country lookup has stored its answer, so a Dutch visitor from Belgium
   * gets nl-BE on a first visit too.
   */
  startAppListening({
    type: UserActionTypes.SET_WHAT_IS_MY_COUNTRY,
    effect: (_action, api) => {
      setPricingLocale(getActiveLanguageSettings().labels.full, getWhatIsMyCountry(api.getState()));
    },
  });

  /**
   * Closes the sub menus and flags the body when rendering the app (same behavior as the former
   * foldInMenuAndFlowClass saga).
   */
  startAppListening({
    type: LOCATION_CHANGE,
    effect: () => {
      foldInMenuAndFlowClass();
    },
  });

  /**
   * Fires the `qa-saga-action` DOM event for the actions QA waits on (same behavior as the former qaSagaAction saga).
   */
  startAppListening({
    predicate: action => QA_ACTION_TYPES.includes(action.type),
    effect: action => {
      document.dispatchEvent(new CustomEvent('qa-saga-action', { detail: action }));
    },
  });
};
